import React, { createContext, useContext, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IDateRangePreset, TDateRangePresetId } from "../interfaces";
import { DEMO_ACCOUNTS, getPresets, LOCAL_STORAGE_KEYS } from "../shared/constants";
import dayjs from "dayjs";
import { useAuth } from "../hooks";

interface DateRangeContextType {
  dateRange: {
    startDate: Date;
    endDate: Date;
  };
  selectedPreset: IDateRangePreset | undefined;
  setDateRange: (startDate: Date, endDate: Date) => void;
  setSelectedPreset: (preset: IDateRangePreset | undefined) => void;
}

const DateRangeContext = createContext<DateRangeContextType | undefined>(
  undefined
);

export const DateRangeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth()
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedPreset, setSelectedPreset] = useState<
    IDateRangePreset | undefined
  >(() => {
    // 1) Try localStorage first
    try {
      const storedPreset = localStorage.getItem(
        LOCAL_STORAGE_KEYS.DATA_RANGE_PRESET
      ) as TDateRangePresetId | null;
      if (storedPreset) {
        const found = getPresets(t).find((p) => p.id === storedPreset);
        if (found) return found;
      }
    } catch { }

    const demoAccounts = DEMO_ACCOUNTS.includes(String(user?.schema));
    const presetType = demoAccounts ? "lastMonth" : "last30Days";

    // 2) Then URL params
    const presetId = searchParams.get("preset") as TDateRangePresetId;
    if (presetId) {
      return getPresets(t).find((p) => p.id === presetId);
    }

    // 3) Fallback to default
    return getPresets(t).find((p) => p.id === presetType);
  });

  const [dateRange, setDateRangeState] = useState<{
    startDate: Date;
    endDate: Date;
  }>(() => {
    // 1) Try localStorage first
    try {
      const lsFrom = localStorage.getItem(LOCAL_STORAGE_KEYS.DATA_RANGE_FROM);
      const lsTo = localStorage.getItem(LOCAL_STORAGE_KEYS.DATA_RANGE_TO);
      if (lsFrom && lsTo) {
        const fromValid = dayjs(lsFrom, "YYYY-MM-DD", true).isValid();
        const toValid = dayjs(lsTo, "YYYY-MM-DD", true).isValid();
        if (fromValid && toValid) {
          return {
            startDate: new Date(lsFrom),
            endDate: new Date(lsTo),
          };
        }
      }
    } catch { }

    // 2) Then URL params
    const from = searchParams.get("from");
    const to = searchParams.get("to");

    if (from && to) {
      return {
        startDate: new Date(from),
        endDate: new Date(to),
      };
    }

    // 3) Fallback to selected preset or today
    const range = selectedPreset?.getRange() || [new Date(), new Date()];
    return {
      startDate: range[0] as Date,
      endDate: range[1] as Date,
    };
  });

  // Update URL params when date range or preset changes
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.set("from", dayjs(dateRange.startDate).format("YYYY-MM-DD"));
    params.set("to", dayjs(dateRange.endDate).format("YYYY-MM-DD"));
    if (selectedPreset) {
      params.set("preset", selectedPreset.id);
    } else {
      params.delete("preset");
    }
    setSearchParams(params, { replace: true });

    // Persist to localStorage as well
    try {
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.DATA_RANGE_FROM,
        dayjs(dateRange.startDate).format("YYYY-MM-DD")
      );
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.DATA_RANGE_TO,
        dayjs(dateRange.endDate).format("YYYY-MM-DD")
      );
      if (selectedPreset) {
        localStorage.setItem(
          LOCAL_STORAGE_KEYS.DATA_RANGE_PRESET,
          selectedPreset.id
        );
      } else {
        localStorage.removeItem(LOCAL_STORAGE_KEYS.DATA_RANGE_PRESET);
      }
    } catch { }
  }, [dateRange, selectedPreset, setSearchParams]);

  const setDateRange = (startDate: Date, endDate: Date) => {
    setDateRangeState({ startDate, endDate });
    setSelectedPreset(undefined); // Clear preset when manually selecting dates
  };

  const value = {
    dateRange,
    selectedPreset,
    setDateRange,
    setSelectedPreset,
  };

  return (
    <DateRangeContext.Provider value={value}>
      {children}
    </DateRangeContext.Provider>
  );
};

export const useDateRange = () => {
  const context = useContext(DateRangeContext);
  if (!context) {
    throw new Error("useDateRange must be used within a DateRangeProvider");
  }
  return context;
};
